import { c as _c } from "react/compiler-runtime";
import { useToast } from '@onefootprint/ui';
import type { OnSelectionChangeParams } from '@xyflow/react';
import { useReactFlow } from '@xyflow/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withSubject } from '../utils/get-subject';
import useStore from './use-store';
const useMultiSelect = () => {
  const $ = _c(14);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const selectedNodeIdsRef = useRef(t0);
  const reactFlow = useReactFlow();
  const {
    toast
  } = useToast();
  const {
    data: t1
  } = useStore();
  const {
    graph
  } = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "edit.multiSelectCopy"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const syncShiftFromKeyboard = e => {
        setIsShiftPressed(e.shiftKey);
      };
      const clearShift = () => {
        setIsShiftPressed(false);
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          clearShift();
        }
      };
      window.addEventListener("keydown", syncShiftFromKeyboard);
      window.addEventListener("keyup", syncShiftFromKeyboard);
      window.addEventListener("blur", clearShift);
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        window.removeEventListener("keydown", syncShiftFromKeyboard);
        window.removeEventListener("keyup", syncShiftFromKeyboard);
        window.removeEventListener("blur", clearShift);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== reactFlow) {
    t5 = t6 => {
      const {
        nodes
      } = t6;
      if (nodes.length <= 1) {
        selectedNodeIdsRef.current = nodes.map(_temp);
        return;
      }
      const firstSubgraphId = nodes[0]?.data?.subgraphId ?? null;
      const sameSubgraphNodes = nodes.filter(n_0 => (n_0.data?.subgraphId ?? null) === firstSubgraphId);
      const hasNodesFromOtherSubgraphs = nodes.length !== sameSubgraphNodes.length;
      if (hasNodesFromOtherSubgraphs) {
        const idsToKeep = new Set(sameSubgraphNodes.map(_temp2));
        reactFlow.setNodes(currentNodes => currentNodes.map(n_2 => idsToKeep.has(n_2.id) ? n_2 : {
          ...n_2,
          selected: false
        }));
      }
      selectedNodeIdsRef.current = sameSubgraphNodes.map(_temp3);
    };
    $[4] = reactFlow;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onSelectionChange = t5;
  let t6;
  let t7;
  if ($[6] !== graph || $[7] !== t || $[8] !== toast) {
    t6 = () => {
      const handleCopyShortcut = e_0 => {
        const isCopy = (e_0.metaKey || e_0.ctrlKey) && e_0.key === "c";
        if (!isCopy || selectedNodeIdsRef.current.length === 0) {
          return;
        }
        const target = e_0.target;
        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLElement && target.isContentEditable) {
          return;
        }
        e_0.preventDefault();
        const nodesToCopy = graph.nodes.filter(node => selectedNodeIdsRef.current.includes(node.id)).map(_temp4).map(node_1 => withSubject(graph)(node_1));
        if (nodesToCopy.length === 0) {
          return;
        }
        navigator.clipboard.writeText(JSON.stringify(nodesToCopy, null, 2));
        const description = nodesToCopy.length > 1 ? t(_temp5) : t(_temp6);
        toast({
          title: t(_temp7),
          description
        });
      };
      window.addEventListener("keydown", handleCopyShortcut);
      return () => window.removeEventListener("keydown", handleCopyShortcut);
    };
    t7 = [graph, toast, t];
    $[6] = graph;
    $[7] = t;
    $[8] = toast;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== isShiftPressed || $[12] !== onSelectionChange) {
    t8 = {
      isShiftPressed,
      onSelectionChange
    };
    $[11] = isShiftPressed;
    $[12] = onSelectionChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
export default useMultiSelect;
function _temp(n) {
  return n.id;
}
function _temp2(n_1) {
  return n_1.id;
}
function _temp3(n_3) {
  return n_3.id;
}
function _temp4(t0) {
  const {
    context: _,
    ...node_0
  } = t0;
  return node_0;
}
function _temp5($) {
  return $.descriptionMultiple;
}
function _temp6($_0) {
  return $_0.descriptionSingle;
}
function _temp7($_1) {
  return $_1.title;
}